import { useState, useEffect, useCallback, useRef } from 'react';
import { renderRegistryBlockEditor } from '../components/vision-deck/blockRegistry';
import '../components/vision-deck/registryTestBlock';
import '../components/vision-deck/blocks/FrameworkMatrixBlock';
import '../components/vision-deck/blocks/whiteboard/WhiteboardBlock';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { normalizeExternalUrl } from '../lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { 
  ArrowLeft,
  Save,
  Eye,
  Send,
  Plus,
  Type,
  Image,
  Video,
  Square,
  Minus,
  MousePointer2,
  Trash2,
  MoveUp,
  MoveDown,
  Settings,
  Palette,
  Download,
  Link2,
  ChevronDown,
  Bold,
  Italic,
  Underline,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Sparkles,
  GripVertical,
  ExternalLink,
  Copy,
  MoreHorizontal,
  Upload,
  X,
  Check,
  Edit3,
  Layers,
  Play,
  ImagePlus,
  Film,
  RefreshCw,
  Undo2,
  FileText,
  Layout,
  SlidersHorizontal,
  // Phase 7.1 — premium block icons
  MessageSquare,
  TrendingUp,
  UserCircle,
  Columns,
  Quote,
  Mail,
  Phone,
  CheckCircle2,
  XCircle,
  // Phase 7.2 — premium block icons
  Timer,
  DollarSign,
  GitCommit,
  HelpCircle,
  Star,
  // Phase 7.3 — interactive premium block icons
  Calculator,
  CalendarCheck,
  ClipboardList,
  PlayCircle,
  Grid3x3,
  PenTool
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

// Import refactored components
import { ImageUploader } from '../components/vision-deck/ImageUploader';
import { VideoBlockEditor, MobileVideoBlockEditor } from '../components/vision-deck/VideoBlockEditors';
import { 
  TEMPLATE_STYLES, 
  BLOCK_TYPES, 
  CTA_PRESETS, 
  FONT_SIZES, 
  getDefaultBlockContent 
} from '../components/vision-deck/constants';

// ============================================
// P1b — SERVICE ADJUSTER VALIDATION
// ============================================
// Returns an object: { errors: { fieldName: msg }, hasErrors: bool, suggestion: { ... } }
// Validation is non-blocking visual feedback + a save gate.
// Legacy decks with invalid data still render — warnings just appear.
export function validateServiceAdjuster(content) {
  const errors = {};
  const min = Number.isFinite(parseInt(content.minQuantity, 10)) ? parseInt(content.minQuantity, 10) : 0;
  const maxRaw = content.maxQuantity;
  const hasMax = maxRaw !== null && maxRaw !== undefined && maxRaw !== '';
  const max = hasMax ? parseInt(maxRaw, 10) : null;
  const adj = Number.isFinite(parseInt(content.adjustableQuantity, 10)) ? parseInt(content.adjustableQuantity, 10) : 0;

  if (min < 0) errors.minQuantity = 'Negative values not allowed';
  if (hasMax && max < 0) errors.maxQuantity = 'Negative values not allowed';

  if (hasMax && min === max) {
    errors.minQuantity = errors.minQuantity || 'Min cannot equal Max — the slider would lock';
    errors.maxQuantity = errors.maxQuantity || 'Max cannot equal Min — the slider would lock';
  }
  if (hasMax && min > max) {
    errors.minQuantity = errors.minQuantity || 'Min must be less than Max';
    errors.maxQuantity = errors.maxQuantity || 'Max must be greater than Min';
  }

  if (adj < min) {
    errors.adjustableQuantity = `Adjustable Quantity must be ≥ Min (${min})`;
  } else if (hasMax && adj > max) {
    errors.adjustableQuantity = `Adjustable Quantity must be ≤ Max (${max})`;
  }

  // Suggested auto-fix
  let suggestion = null;
  if (Object.keys(errors).length > 0) {
    const fixedMin = Math.max(0, min);
    let fixedMax;
    if (!hasMax || max <= fixedMin) {
      fixedMax = fixedMin + 1;
    } else {
      fixedMax = max;
    }
    let fixedAdj = adj;
    if (fixedAdj < fixedMin) fixedAdj = fixedMin;
    if (fixedAdj > fixedMax) fixedAdj = fixedMax;
    suggestion = {
      minQuantity: fixedMin,
      maxQuantity: fixedMax,
      adjustableQuantity: fixedAdj,
    };
  }

  return { errors, hasErrors: Object.keys(errors).length > 0, suggestion };
}

// Walks all pages/blocks and returns true if any service-adjuster block has invalid values.
export function deckHasInvalidAdjusters(deck) {
  if (!deck?.pages) return false;
  for (const page of deck.pages) {
    for (const block of page.blocks || []) {
      if (block.type === 'service-adjuster') {
        const v = validateServiceAdjuster(block.content || {});
        if (v.hasErrors) return true;
      }
    }
  }
  return false;
}

// ============================================
// BLOCK RENDERER COMPONENT
// ============================================
function BlockRenderer({ block, style, isEditing, onUpdate, onDelete, onDuplicate, onMoveUp, onMoveDown, isFirst, isLast, pages }) {
  const [isHovered, setIsHovered] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const renderBlockContent = () => {
    switch (block.type) {
      case 'text':
        const textAlign = block.content.align || 'left';
        return (
          <div className="w-full">
            {isEditing ? (
              <div className="space-y-3">
                <Textarea
                  value={block.content.content}
                  onChange={(e) => onUpdate({ ...block, content: { ...block.content, content: e.target.value } })}
                  placeholder="Start typing your content..."
                  className={`w-full min-h-[120px] bg-white/80 border-gray-200 resize-none focus:border-[#B89B5E] focus:ring-[#B89B5E]/20 ${block.content.font} ${block.content.size} ${block.content.bold ? 'font-bold' : ''} ${block.content.italic ? 'italic' : ''}`}
                  style={{ textAlign: textAlign, color: block.content.color || style.text }}
                />
                {/* Text Formatting Toolbar */}
                <div className="flex items-center gap-1 flex-wrap">
                  <Select value={block.content.size} onValueChange={(v) => onUpdate({ ...block, content: { ...block.content, size: v } })}>
                    <SelectTrigger className="w-24 h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {FONT_SIZES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <div className="flex border rounded-md overflow-hidden">
                    <button onClick={() => onUpdate({ ...block, content: { ...block.content, bold: !block.content.bold } })}
                      className={`p-1.5 ${block.content.bold ? 'bg-[#B89B5E]/20 text-[#B89B5E]' : 'hover:bg-gray-100'}`}>
                      <Bold className="w-4 h-4" />
                    </button>
                    <button onClick={() => onUpdate({ ...block, content: { ...block.content, italic: !block.content.italic } })}
                      className={`p-1.5 ${block.content.italic ? 'bg-[#B89B5E]/20 text-[#B89B5E]' : 'hover:bg-gray-100'}`}>
                      <Italic className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="flex border rounded-md overflow-hidden">
                    {['left', 'center', 'right'].map(align => (
                      <button key={align} onClick={() => onUpdate({ ...block, content: { ...block.content, align } })}
                        className={`p-1.5 ${block.content.align === align ? 'bg-[#B89B5E]/20 text-[#B89B5E]' : 'hover:bg-gray-100'}`}>
                        {align === 'left' && <AlignLeft className="w-4 h-4" />}
                        {align === 'center' && <AlignCenter className="w-4 h-4" />}
                        {align === 'right' && <AlignRight className="w-4 h-4" />}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <p 
                className={`whitespace-pre-wrap w-full ${block.content.font} ${block.content.size} ${block.content.bold ? 'font-bold' : ''} ${block.content.italic ? 'italic' : ''} ${block.content.underline ? 'underline' : ''}`}
                style={{ textAlign: textAlign, color: block.content.color || style.text }}
              >
                {block.content.content || <span className="text-gray-400 italic">Empty text block</span>}
              </p>
            )}
          </div>
        );

      case 'image':
        return isEditing ? (
          <ImageUploader
            value={block.content.url}
            onChange={(url) => onUpdate({ ...block, content: { ...block.content, url } })}
            onRemove={() => onUpdate({ ...block, content: { ...block.content, url: '' } })}
            placeholder="Add your image"
          />
        ) : block.content.url ? (
          <div className={`rounded-lg overflow-hidden ${block.content.frame?.shadow ? 'shadow-xl' : ''}`}
            style={{ borderWidth: block.content.frame?.borderWidth, borderColor: block.content.frame?.borderColor, borderStyle: 'solid', borderRadius: block.content.frame?.borderRadius }}>
            <img src={block.content.url} alt={block.content.alt || 'Image'} className="w-full h-auto" />
          </div>
        ) : (
          <div className="aspect-video bg-gray-100 rounded-lg flex items-center justify-center">
            <Image className="w-12 h-12 text-gray-300" />
          </div>
        );

      case 'video':
        return isEditing ? (
          <VideoBlockEditor block={block} onUpdate={onUpdate} style={style} />
        ) : block.content.thumbnailUrl ? (
          <div className="relative rounded-lg overflow-hidden cursor-pointer group" style={{ borderRadius: block.content.frame?.borderRadius }}>
            <img src={block.content.thumbnailUrl} alt="Video" className="w-full aspect-video object-cover" />
            <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/40 transition-colors">
              <div className="w-20 h-20 rounded-full bg-white/90 flex items-center justify-center shadow-lg transform group-hover:scale-110 transition-transform">
                <Play className="w-10 h-10 text-[#B89B5E] ml-1" />
              </div>
            </div>
          </div>
        ) : block.content.url ? (
          <video src={block.content.url} controls className="w-full rounded-lg" />
        ) : (
          <div className="aspect-video bg-gray-100 rounded-lg flex items-center justify-center">
            <Film className="w-12 h-12 text-gray-300" />
          </div>
        );

      case 'logo':
        return isEditing ? (
          <div className="text-center">
            <ImageUploader
              value={block.content.url}
              onChange={(url) => onUpdate({ ...block, content: { ...block.content, url } })}
              onRemove={() => onUpdate({ ...block, content: { ...block.content, url: '' } })}
              placeholder="Upload your logo"
            />
            {block.content.url && (
              <div className="mt-3 flex justify-center gap-2">
                {['small', 'medium', 'large'].map(size => (
                  <button key={size} onClick={() => onUpdate({ ...block, content: { ...block.content, size } })}
                    className={`px-3 py-1 text-xs rounded capitalize ${block.content.size === size ? 'bg-[#B89B5E] text-white' : 'bg-gray-100 hover:bg-gray-200'}`}>
                    {size}
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : block.content.url ? (
          <div style={{ textAlign: block.content.align }}>
            <img src={block.content.url} alt="Logo" className={`inline-block ${block.content.size === 'small' ? 'h-12' : block.content.size === 'medium' ? 'h-20' : 'h-32'}`} />
          </div>
        ) : (
          <div className="flex items-center justify-center py-8">
            <div className="w-20 h-20 bg-gray-100 rounded-lg flex items-center justify-center">
              <Square className="w-10 h-10 text-gray-300" />
            </div>
          </div>
        );

      case 'divider':
        return (
          <div className="py-4" style={{ textAlign: 'center' }}>
            <hr style={{ width: block.content.width, borderTopWidth: block.content.thickness, borderTopStyle: block.content.style, borderTopColor: block.content.color, margin: '0 auto' }} />
          </div>
        );

      case 'cta':
        return (
          <div style={{ textAlign: block.content.align }} className={isEditing ? 'space-y-4' : ''}>
            {isEditing && (
              <div className="space-y-3">
                {/* Button Text with Presets */}
                <div className="flex flex-col gap-2">
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Button Text</label>
                  <div className="flex gap-2 flex-wrap justify-center">
                    <Input 
                      value={block.content.text} 
                      onChange={(e) => onUpdate({ ...block, content: { ...block.content, text: e.target.value } })} 
                      placeholder="Enter button text" 
                      className="max-w-xs"
                    />
                  </div>
                  <div className="flex flex-wrap gap-1.5 justify-center">
                    {CTA_PRESETS.map((preset) => (
                      <button
                        key={preset.value}
                        onClick={() => onUpdate({ ...block, content: { ...block.content, text: preset.value } })}
                        className={`px-2 py-1 text-xs rounded-full transition-all ${
                          block.content.text === preset.value 
                            ? 'bg-[#B89B5E] text-white' 
                            : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
                        }`}
                      >
                        {preset.label}
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Link Type Selection */}
                <div className="flex flex-col gap-2">
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Link Type</label>
                  <div className="flex gap-2 justify-center">
                    <button
                      onClick={() => onUpdate({ ...block, content: { ...block.content, linkType: 'external', targetPageId: null } })}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                        block.content.linkType !== 'page'
                          ? 'bg-[#B89B5E] text-white ring-2 ring-[#B89B5E]/50' 
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      <ExternalLink className="w-4 h-4" />
                      External URL
                    </button>
                    <button
                      onClick={() => onUpdate({ ...block, content: { ...block.content, linkType: 'page', url: '' } })}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                        block.content.linkType === 'page'
                          ? 'bg-[#B89B5E] text-white ring-2 ring-[#B89B5E]/50' 
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      <Layers className="w-4 h-4" />
                      Link to Page
                    </button>
                  </div>
                </div>

                {/* External URL or Page Selection */}
                {block.content.linkType === 'page' ? (
                  <div className="flex flex-col gap-2">
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Target Page</label>
                    <Select 
                      value={block.content.targetPageId || ''} 
                      onValueChange={(v) => onUpdate({ ...block, content: { ...block.content, targetPageId: v } })}
                    >
                      <SelectTrigger className="max-w-md mx-auto">
                        <SelectValue placeholder="Select a page to link to..." />
                      </SelectTrigger>
                      <SelectContent>
                        {pages?.map((page, index) => (
                          <SelectItem key={page.id} value={page.id}>
                            {page.name || `Page ${index + 1}`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-400 text-center">
                      When clicked, the viewer will scroll to the selected page
                    </p>
                  </div>
                ) : (
                  <div className="flex flex-col gap-2">
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Link URL</label>
                    <Input 
                      value={block.content.url} 
                      onChange={(e) => onUpdate({ ...block, content: { ...block.content, url: e.target.value } })} 
                      placeholder="https://example.com" 
                      className="max-w-md mx-auto"
                    />
                  </div>
                )}

                {/* Button Style */}
                <div className="flex gap-2 justify-center">
                  <button
                    onClick={() => onUpdate({ ...block, content: { ...block.content, style: 'primary' } })}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      block.content.style === 'primary' 
                        ? 'bg-[#B89B5E] text-white ring-2 ring-[#B89B5E]/50' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    Solid Fill
                  </button>
                  <button
                    onClick={() => onUpdate({ ...block, content: { ...block.content, style: 'outline' } })}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all border-2 ${
                      block.content.style !== 'primary' 
                        ? 'border-[#B89B5E] text-[#B89B5E] ring-2 ring-[#B89B5E]/30' 
                        : 'border-gray-300 text-gray-600 hover:border-gray-400'
                    }`}
                  >
                    Outline
                  </button>
                </div>
              </div>
            )}
            <button className={`px-8 py-3 rounded-lg font-medium transition-all ${block.content.style === 'primary' ? 'text-white shadow-lg hover:shadow-xl' : 'border-2'}`}
              style={{ backgroundColor: block.content.style === 'primary' ? style.accent : 'transparent', borderColor: style.accent, color: block.content.style === 'primary' ? '#FFFFFF' : style.accent }}>
              {block.content.text || 'Button'}
              {block.content.linkType === 'page' && block.content.targetPageId && (
                <span className="ml-2 text-xs opacity-70">(→ page link)</span>
              )}
            </button>
          </div>
        );

      case 'mobile-video':
        // Vertical mobile video block (9:16 aspect ratio)
        return isEditing ? (
          <div className="flex justify-center">
            <div className="max-w-md w-full">
              <MobileVideoBlockEditor block={block} onUpdate={onUpdate} style={style} />
            </div>
          </div>
        ) : block.content.url ? (
          <div className="flex justify-center">
            <div className="relative max-w-md w-full rounded-2xl overflow-hidden shadow-xl">
              {block.content.thumbnailUrl ? (
                <div className="relative">
                  <img 
                    src={block.content.thumbnailUrl} 
                    alt="Video thumbnail" 
                    className="w-full aspect-[9/16] object-cover" 
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                    <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
                      <Play className="w-8 h-8 text-[#B89B5E] ml-1" fill="currentColor" />
                    </div>
                  </div>
                </div>
              ) : (
                <video 
                  src={block.content.url} 
                  className="w-full aspect-[9/16] object-cover bg-black"
                  muted
                />
              )}
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-black/60 text-white text-xs">
                Mobile Video
              </div>
            </div>
          </div>
        ) : (
          <div className="flex justify-center">
            <div className="max-w-md w-full aspect-[9/16] bg-gray-100 rounded-2xl flex items-center justify-center">
              <div className="text-center">
                <Play className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-400">Mobile Video</p>
                <p className="text-xs text-gray-300">9:16 Vertical</p>
              </div>
            </div>
          </div>
        );

      case 'service-adjuster':
        // Service Adjuster Block - Interactive quantity controls for deck viewers
        const content = block.content;
        const getIncrementValue = () => {
          switch (content.incrementMode) {
            case 'single': return 1;
            case 'custom': return content.customStep || 1;
            case '10': return 10;
            case '20': return 20;
            case '50': return 50;
            case '100': return 100;
            default: return 1;
          }
        };
        
        return isEditing ? (
          <div className="border border-[#B89B5E]/30 rounded-xl p-5 bg-gradient-to-br from-amber-50/50 to-white space-y-4">
            <div className="flex items-center gap-2 pb-3 border-b border-amber-200/50">
              <SlidersHorizontal className="w-5 h-5 text-[#B89B5E]" />
              <span className="font-medium text-gray-800">Service Adjuster</span>
              <span className="text-xs text-gray-500 ml-auto">Recipients can adjust quantities</span>
            </div>
            
            {/* Service Name & Category */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Service Name *</label>
                <Input
                  value={content.serviceName}
                  onChange={(e) => onUpdate({ ...block, content: { ...content, serviceName: e.target.value } })}
                  placeholder="e.g., Shop Towels"
                  className="bg-white"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Category (optional)</label>
                <Input
                  value={content.category}
                  onChange={(e) => onUpdate({ ...block, content: { ...content, category: e.target.value } })}
                  placeholder="e.g., Towel Service"
                  className="bg-white"
                />
              </div>
            </div>
            
            {/* Description */}
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Description (optional)</label>
              <Textarea
                value={content.description}
                onChange={(e) => onUpdate({ ...block, content: { ...content, description: e.target.value } })}
                placeholder="Brief description of this service..."
                rows={2}
                className="bg-white"
              />
            </div>
            
            {/* Quantities (with P1b validation) */}
            {(() => {
              const validation = validateServiceAdjuster(content);
              const errCls = (field) => validation.errors[field] ? 'bg-white border-red-400 focus-visible:ring-red-300' : 'bg-white';
              const errMsg = (field) => validation.errors[field] && (
                <p className="text-xs text-red-600 mt-1">{validation.errors[field]}</p>
              );
              return (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div>
                      <label className="text-xs font-medium text-gray-600 mb-1 block">Current Qty</label>
                      <Input
                        type="number"
                        value={content.currentQuantity}
                        onChange={(e) => onUpdate({ ...block, content: { ...content, currentQuantity: parseInt(e.target.value) || 0 } })}
                        className="bg-white"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-600 mb-1 block">Starting Adjust Qty</label>
                      <Input
                        type="number"
                        value={content.adjustableQuantity}
                        onChange={(e) => onUpdate({ ...block, content: { ...content, adjustableQuantity: parseInt(e.target.value) || 0 } })}
                        className={errCls('adjustableQuantity')}
                        data-testid="adjuster-adjustable"
                      />
                      {errMsg('adjustableQuantity')}
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-600 mb-1 block">Min Qty</label>
                      <Input
                        type="number"
                        value={content.minQuantity}
                        onChange={(e) => onUpdate({ ...block, content: { ...content, minQuantity: parseInt(e.target.value) || 0 } })}
                        className={errCls('minQuantity')}
                        data-testid="adjuster-min"
                      />
                      {errMsg('minQuantity')}
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-600 mb-1 block">Max Qty (optional)</label>
                      <Input
                        type="number"
                        value={content.maxQuantity || ''}
                        onChange={(e) => onUpdate({ ...block, content: { ...content, maxQuantity: e.target.value ? parseInt(e.target.value) : null } })}
                        placeholder="No limit"
                        className={errCls('maxQuantity')}
                        data-testid="adjuster-max"
                      />
                      {errMsg('maxQuantity')}
                    </div>
                  </div>

                  {/* Optional auto-fix banner */}
                  {validation.hasErrors && validation.suggestion && (
                    <div
                      className="flex items-start justify-between gap-3 p-3 rounded-md bg-amber-50 border border-amber-200"
                      data-testid="adjuster-autofix-banner"
                    >
                      <div className="text-xs text-amber-800">
                        <strong>Slider would lock or break with current values.</strong>{' '}
                        Suggested fix: Min={validation.suggestion.minQuantity}, Max={validation.suggestion.maxQuantity}, Adjustable={validation.suggestion.adjustableQuantity}.
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="text-xs whitespace-nowrap"
                        onClick={() => onUpdate({ ...block, content: { ...content, ...validation.suggestion } })}
                        data-testid="adjuster-autofix-btn"
                      >
                        Fix automatically
                      </Button>
                    </div>
                  )}
                </>
              );
            })()}
            
            {/* Increment & Unit */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Increment Mode</label>
                <Select value={content.incrementMode} onValueChange={(v) => onUpdate({ ...block, content: { ...content, incrementMode: v } })}>
                  <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="single">Single Units (1)</SelectItem>
                    <SelectItem value="custom">Custom Step</SelectItem>
                    <SelectItem value="10">By 10</SelectItem>
                    <SelectItem value="20">By 20</SelectItem>
                    <SelectItem value="50">By 50</SelectItem>
                    <SelectItem value="100">By 100</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {content.incrementMode === 'custom' && (
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Custom Step</label>
                  <Input
                    type="number"
                    value={content.customStep}
                    onChange={(e) => onUpdate({ ...block, content: { ...content, customStep: parseInt(e.target.value) || 1 } })}
                    className="bg-white"
                  />
                </div>
              )}
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Unit Label</label>
                <Input
                  value={content.unitLabel}
                  onChange={(e) => onUpdate({ ...block, content: { ...content, unitLabel: e.target.value } })}
                  placeholder="e.g., mats, towels, garments"
                  className="bg-white"
                />
              </div>
            </div>
            
            {/* Options */}
            <div className="flex items-center gap-4 pt-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={content.allowProspectNote}
                  onChange={(e) => onUpdate({ ...block, content: { ...content, allowProspectNote: e.target.checked } })}
                  className="rounded border-gray-300 text-[#B89B5E] focus:ring-[#B89B5E]"
                />
                <span className="text-sm text-gray-600">Allow recipient to add note</span>
              </label>
            </div>
            
            {/* Internal Note */}
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Internal Note (not visible to recipient)</label>
              <Input
                value={content.internalNote}
                onChange={(e) => onUpdate({ ...block, content: { ...content, internalNote: e.target.value } })}
                placeholder="Notes for your reference only..."
                className="bg-white"
              />
            </div>
            
            {/* Preview */}
            <div className="mt-4 pt-4 border-t border-amber-200/50">
              <p className="text-xs text-gray-500 mb-3">Preview (how recipient will see it):</p>
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="font-medium text-gray-800">{content.serviceName || 'Service Name'}</p>
                    {content.category && <p className="text-xs text-gray-500">{content.category}</p>}
                  </div>
                  {content.currentQuantity > 0 && (
                    <div className="text-right">
                      <p className="text-xs text-gray-500">Current</p>
                      <p className="font-semibold text-gray-700">{content.currentQuantity} {content.unitLabel}</p>
                    </div>
                  )}
                </div>
                {content.description && <p className="text-sm text-gray-600 mb-3">{content.description}</p>}
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-600">Adjust:</span>
                  <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-2 py-1">
                    <button className="w-8 h-8 rounded-md bg-[#B89B5E] text-white flex items-center justify-center">-</button>
                    <span className="w-16 text-center font-semibold">{content.adjustableQuantity || 0}</span>
                    <button className="w-8 h-8 rounded-md bg-[#B89B5E] text-white flex items-center justify-center">+</button>
                  </div>
                  <span className="text-sm text-gray-500">{content.unitLabel}</span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          // Non-editing preview
          <div className="border border-[#B89B5E]/30 rounded-xl p-5 bg-gradient-to-br from-amber-50/50 to-white">
            <div className="flex items-center justify-between mb-2">
              <div>
                <p className="font-medium text-gray-800">{content.serviceName || 'Service Name'}</p>
                {content.category && <p className="text-xs text-gray-500">{content.category}</p>}
              </div>
              {content.currentQuantity > 0 && (
                <div className="text-right">
                  <p className="text-xs text-gray-500">Current</p>
                  <p className="font-semibold text-gray-700">{content.currentQuantity} {content.unitLabel}</p>
                </div>
              )}
            </div>
            {content.description && <p className="text-sm text-gray-600 mb-3">{content.description}</p>}
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600">Adjust:</span>
              <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-2 py-1">
                <button className="w-8 h-8 rounded-md bg-[#B89B5E] text-white flex items-center justify-center">-</button>
                <span className="w-16 text-center font-semibold">{content.adjustableQuantity || 0}</span>
                <button className="w-8 h-8 rounded-md bg-[#B89B5E] text-white flex items-center justify-center">+</button>
              </div>
              <span className="text-sm text-gray-500">{content.unitLabel}</span>
              <span className="text-xs text-gray-400 ml-2">(+/- {getIncrementValue()})</span>
            </div>
          </div>
        );

      case 'testimonial': {
        const t = block.content;
        return isEditing ? (
          <div className="border border-[#B89B5E]/30 rounded-xl p-5 bg-gradient-to-br from-amber-50/40 to-white space-y-4">
            <div className="flex items-center gap-2 pb-3 border-b border-amber-200/50">
              <Quote className="w-5 h-5 text-[#B89B5E]" />
              <span className="font-medium text-gray-800">Testimonial</span>
            </div>
            <div>
              <Quote className="w-6 h-6 mb-1" style={{ color: style.accent }} />
              <Textarea
                value={t.quote}
                onChange={(e) => onUpdate({ ...block, content: { ...t, quote: e.target.value } })}
                placeholder="Customer quote..."
                rows={3}
                className="bg-white italic"
                data-testid="testimonial-quote"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Attribution Name</label>
                <Input value={t.attribution_name || ''} onChange={(e) => onUpdate({ ...block, content: { ...t, attribution_name: e.target.value } })} placeholder="Jane Smith" className="bg-white" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Attribution Title</label>
                <Input value={t.attribution_title || ''} onChange={(e) => onUpdate({ ...block, content: { ...t, attribution_title: e.target.value } })} placeholder="VP of Sales" className="bg-white" />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Company</label>
              <Input value={t.attribution_company || ''} onChange={(e) => onUpdate({ ...block, content: { ...t, attribution_company: e.target.value } })} placeholder="Acme Corp" className="bg-white" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-gray-600 mb-2 block">Headshot Photo (optional)</label>
                <ImageUploader
                  value={t.photo_url}
                  onChange={(url) => onUpdate({ ...block, content: { ...t, photo_url: url } })}
                  onRemove={() => onUpdate({ ...block, content: { ...t, photo_url: '' } })}
                  placeholder="Upload headshot"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-2 block">Company Logo (optional)</label>
                <ImageUploader
                  value={t.logo_url}
                  onChange={(url) => onUpdate({ ...block, content: { ...t, logo_url: url } })}
                  onRemove={() => onUpdate({ ...block, content: { ...t, logo_url: '' } })}
                  placeholder="Upload logo"
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="rounded-xl p-5 md:p-6 border-l-4 bg-white/60" style={{ borderLeftColor: style.accent, backgroundColor: style.secondaryBg }}>
            <Quote className="w-7 h-7 mb-2" style={{ color: style.accent }} />
            <p className="italic text-base md:text-lg font-serif leading-relaxed mb-3" style={{ color: style.text }}>
              {t.quote || <span className="opacity-40">Customer quote...</span>}
            </p>
            <div className="h-px w-16 mb-3" style={{ backgroundColor: style.accent }} />
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3 min-w-0">
                {t.photo_url ? (
                  <img src={t.photo_url} alt={t.attribution_name || ''} className="w-10 h-10 rounded-full object-cover flex-shrink-0" style={{ outline: `2px solid ${style.accent}`, outlineOffset: 1 }} />
                ) : null}
                <div className="text-sm min-w-0">
                  <span className="font-semibold" style={{ color: style.text }}>{t.attribution_name || 'Name'}</span>
                  {(t.attribution_title || t.attribution_company) && (
                    <span style={{ color: style.muted }}>
                      {' · '}{t.attribution_title}{t.attribution_company ? ` · ${t.attribution_company}` : ''}
                    </span>
                  )}
                </div>
              </div>
              {t.logo_url && (
                <img src={t.logo_url} alt={t.attribution_company || ''} className="h-8 max-h-8 w-auto object-contain opacity-80" />
              )}
            </div>
          </div>
        );
      }

      case 'stats': {
        const s = block.content;
        const stats = Array.isArray(s.stats) ? s.stats : [];
        const updateStat = (idx, patch) => {
          const next = [...stats];
          next[idx] = { ...next[idx], ...patch };
          onUpdate({ ...block, content: { ...s, stats: next } });
        };
        const removeStat = (idx) => {
          onUpdate({ ...block, content: { ...s, stats: stats.filter((_, i) => i !== idx) } });
        };
        const addStat = () => {
          if (stats.length >= 4) return;
          onUpdate({ ...block, content: { ...s, stats: [...stats, { value: '', label: '' }] } });
        };
        return isEditing ? (
          <div className="border border-[#B89B5E]/30 rounded-xl p-5 bg-gradient-to-br from-amber-50/40 to-white space-y-4">
            <div className="flex items-center gap-2 pb-3 border-b border-amber-200/50">
              <TrendingUp className="w-5 h-5 text-[#B89B5E]" />
              <span className="font-medium text-gray-800">Stats &amp; Metrics</span>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Headline (optional)</label>
              <Input value={s.headline || ''} onChange={(e) => onUpdate({ ...block, content: { ...s, headline: e.target.value } })} placeholder="The Impact" className="bg-white" data-testid="stats-headline" />
            </div>
            <div className="space-y-3">
              {stats.map((st, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-5">
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Value #{idx + 1}</label>
                    <Input value={st.value || ''} onChange={(e) => updateStat(idx, { value: e.target.value })} placeholder="30%" className="bg-white text-lg font-semibold" />
                  </div>
                  <div className="col-span-6">
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Label</label>
                    <Input value={st.label || ''} onChange={(e) => updateStat(idx, { label: e.target.value })} placeholder="Faster Proposals" className="bg-white" />
                  </div>
                  <div className="col-span-1 flex justify-end">
                    <Button type="button" variant="ghost" size="icon" className="h-9 w-9 text-red-500 hover:text-red-600 hover:bg-red-50" onClick={() => removeStat(idx)} data-testid={`stat-remove-${idx}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            {stats.length < 4 && (
              <Button type="button" variant="outline" size="sm" onClick={addStat} className="w-full border-dashed" data-testid="stat-add-btn">
                <Plus className="h-4 w-4 mr-1" /> Add Stat ({stats.length}/4)
              </Button>
            )}
          </div>
        ) : (
          <div className="rounded-xl p-5 md:p-8 text-center" style={{ backgroundColor: style.secondaryBg }}>
            {s.headline && (
              <h3 className="font-serif text-xl md:text-2xl mb-5 md:mb-6 tracking-wide" style={{ color: style.text }}>
                {s.headline}
              </h3>
            )}
            <div className={`grid grid-cols-2 ${stats.length >= 3 ? 'md:grid-cols-3' : ''} ${stats.length === 4 ? 'md:grid-cols-4' : ''} gap-4 md:gap-2`}>
              {stats.map((st, idx) => (
                <div key={idx} className={`px-2 md:px-4 ${idx > 0 ? 'md:border-l' : ''}`} style={{ borderColor: `${style.accent}40` }}>
                  <div className="font-serif text-3xl md:text-5xl font-bold leading-none mb-2" style={{ color: style.accent }}>
                    {st.value || '—'}
                  </div>
                  <div className="text-xs md:text-sm uppercase tracking-widest" style={{ color: style.muted }}>
                    {st.label || ''}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      }

      case 'contact-card': {
        const c = block.content;
        const fillFromProfile = () => {
          // Pull whatever we know about the current user; leave gaps untouched.
          let token = null;
          try { token = localStorage.getItem('token'); } catch {}
          fetch(`${API}/api/auth/me`, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
            .then((r) => r.ok ? r.json() : null)
            .then((me) => {
              if (!me) return;
              onUpdate({
                ...block,
                content: {
                  ...c,
                  name: c.name || me.name || '',
                  email: c.email || me.email || '',
                  // these are best-effort — leave existing values when API doesn't have them
                  title: c.title || me.title || me.role_label || '',
                  phone: c.phone || me.phone || '',
                  photo_url: c.photo_url || me.avatar_url || me.photo_url || '',
                },
              });
              toast.success('Filled from your profile');
            })
            .catch(() => {});
        };
        const charCount = (c.bio || '').length;
        return isEditing ? (
          <div className="border border-[#B89B5E]/30 rounded-xl p-5 bg-gradient-to-br from-amber-50/40 to-white space-y-4">
            <div className="flex items-center justify-between gap-2 pb-3 border-b border-amber-200/50">
              <div className="flex items-center gap-2">
                <UserCircle className="w-5 h-5 text-[#B89B5E]" />
                <span className="font-medium text-gray-800">Your Contact</span>
              </div>
              <Button type="button" variant="outline" size="sm" onClick={fillFromProfile} className="text-xs" data-testid="contact-fill-from-profile">
                <Sparkles className="h-3 w-3 mr-1" /> Fill from my profile
              </Button>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-2 block">Headshot (optional)</label>
              <ImageUploader
                value={c.photo_url}
                onChange={(url) => onUpdate({ ...block, content: { ...c, photo_url: url } })}
                onRemove={() => onUpdate({ ...block, content: { ...c, photo_url: '' } })}
                placeholder="Upload headshot"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Name</label>
              <Input value={c.name || ''} onChange={(e) => onUpdate({ ...block, content: { ...c, name: e.target.value } })} placeholder="Your name" className="bg-white text-base" data-testid="contact-name" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Title</label>
              <Input value={c.title || ''} onChange={(e) => onUpdate({ ...block, content: { ...c, title: e.target.value } })} placeholder="Senior Account Executive" className="bg-white" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-medium text-gray-600">Email</label>
                  <label className="flex items-center gap-1 text-xs text-gray-500 cursor-pointer">
                    <input type="checkbox" checked={c.show_email !== false} onChange={(e) => onUpdate({ ...block, content: { ...c, show_email: e.target.checked } })} className="rounded border-gray-300 text-[#B89B5E]" />
                    show
                  </label>
                </div>
                <Input type="email" value={c.email || ''} onChange={(e) => onUpdate({ ...block, content: { ...c, email: e.target.value } })} placeholder="you@company.com" className="bg-white" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-medium text-gray-600">Phone</label>
                  <label className="flex items-center gap-1 text-xs text-gray-500 cursor-pointer">
                    <input type="checkbox" checked={c.show_phone !== false} onChange={(e) => onUpdate({ ...block, content: { ...c, show_phone: e.target.checked } })} className="rounded border-gray-300 text-[#B89B5E]" />
                    show
                  </label>
                </div>
                <Input value={c.phone || ''} onChange={(e) => onUpdate({ ...block, content: { ...c, phone: e.target.value } })} placeholder="(555) 555-1234" className="bg-white" />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-medium text-gray-600">Bio (1-2 sentences)</label>
                <span className={`text-xs ${charCount > 120 ? 'text-red-600 font-semibold' : 'text-gray-400'}`}>{charCount}/120</span>
              </div>
              <Textarea value={c.bio || ''} onChange={(e) => onUpdate({ ...block, content: { ...c, bio: e.target.value.slice(0, 200) } })} placeholder="A short personal note that builds trust..." rows={2} className="bg-white italic" />
              <label className="flex items-center gap-2 mt-2 text-xs text-gray-500 cursor-pointer">
                <input type="checkbox" checked={c.show_bio !== false} onChange={(e) => onUpdate({ ...block, content: { ...c, show_bio: e.target.checked } })} className="rounded border-gray-300 text-[#B89B5E]" />
                Show bio on public deck
              </label>
            </div>
          </div>
        ) : (
          <div className="rounded-xl p-4 md:p-6 border flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-6" style={{ backgroundColor: style.secondaryBg, borderColor: `${style.accent}40` }}>
            <div className="flex-shrink-0">
              {c.photo_url ? (
                <img src={c.photo_url} alt={c.name || ''} className="w-20 h-20 md:w-24 md:h-24 rounded-full object-cover" style={{ outline: `2px solid ${style.accent}`, outlineOffset: 2 }} />
              ) : (
                <div className="w-20 h-20 md:w-24 md:h-24 rounded-full flex items-center justify-center" style={{ backgroundColor: `${style.accent}20`, outline: `2px solid ${style.accent}`, outlineOffset: 2 }}>
                  <UserCircle className="w-12 h-12" style={{ color: style.accent }} />
                </div>
              )}
            </div>
            <div className="min-w-0 flex-1 w-full">
              {c.name && <div className="font-serif text-xl md:text-2xl font-semibold mb-0.5" style={{ color: style.text }}>{c.name}</div>}
              {c.title && <div className="text-xs uppercase tracking-widest mb-2" style={{ color: style.accent }}>{c.title}</div>}
              {c.show_bio !== false && c.bio && (
                <p className="text-sm md:text-base italic mb-3" style={{ color: style.muted }}>{c.bio}</p>
              )}
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-sm">
                {c.show_email !== false && c.email && (
                  <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1.5 hover:underline" style={{ color: style.text }}>
                    <Mail className="w-4 h-4" style={{ color: style.accent }} />
                    {c.email}
                  </a>
                )}
                {c.show_phone !== false && c.phone && (
                  <a href={`tel:${(c.phone || '').replace(/[^0-9+]/g, '')}`} className="inline-flex items-center gap-1.5 hover:underline" style={{ color: style.text }}>
                    <Phone className="w-4 h-4" style={{ color: style.accent }} />
                    {c.phone}
                  </a>
                )}
              </div>
            </div>
          </div>
        );
      }

      case 'comparison': {
        const cmp = block.content;
        const rows = Array.isArray(cmp.rows) ? cmp.rows : [];
        const updateRow = (idx, patch) => {
          const next = [...rows];
          next[idx] = { ...next[idx], ...patch };
          onUpdate({ ...block, content: { ...cmp, rows: next } });
        };
        const removeRow = (idx) => onUpdate({ ...block, content: { ...cmp, rows: rows.filter((_, i) => i !== idx) } });
        const addRow = () => {
          if (rows.length >= 8) return;
          onUpdate({ ...block, content: { ...cmp, rows: [...rows, { left: '', right: '' }] } });
        };
        const setMode = (mode) => {
          // Only swap default labels when both are still defaults; preserve custom labels.
          const beforeAfter = cmp.left_label === 'Without Us' || cmp.right_label === 'With Us';
          const usVsThem = cmp.left_label === 'Them' || cmp.right_label === 'Us';
          let nextLeft = cmp.left_label;
          let nextRight = cmp.right_label;
          if (mode === 'us-vs-them' && beforeAfter) { nextLeft = 'Them'; nextRight = 'Us'; }
          if (mode === 'before-after' && usVsThem) { nextLeft = 'Without Us'; nextRight = 'With Us'; }
          onUpdate({ ...block, content: { ...cmp, mode, left_label: nextLeft, right_label: nextRight } });
        };
        return isEditing ? (
          <div className="border border-[#B89B5E]/30 rounded-xl p-5 bg-gradient-to-br from-amber-50/40 to-white space-y-4">
            <div className="flex items-center gap-2 pb-3 border-b border-amber-200/50">
              <Columns className="w-5 h-5 text-[#B89B5E]" />
              <span className="font-medium text-gray-800">Before &amp; After</span>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Headline (optional)</label>
              <Input value={cmp.headline || ''} onChange={(e) => onUpdate({ ...block, content: { ...cmp, headline: e.target.value } })} placeholder="The Difference" className="bg-white" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Mode</label>
              <div className="flex gap-2">
                <button type="button" onClick={() => setMode('before-after')} className={`px-3 py-1.5 rounded-md text-sm transition ${cmp.mode === 'before-after' ? 'bg-[#B89B5E] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-testid="comparison-mode-before-after">Before &amp; After</button>
                <button type="button" onClick={() => setMode('us-vs-them')} className={`px-3 py-1.5 rounded-md text-sm transition ${cmp.mode === 'us-vs-them' ? 'bg-[#B89B5E] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-testid="comparison-mode-us-vs-them">Us vs Them</button>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Left Column Label</label>
                <Input value={cmp.left_label || ''} onChange={(e) => onUpdate({ ...block, content: { ...cmp, left_label: e.target.value } })} placeholder="Without Us" className="bg-white" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Right Column Label</label>
                <Input value={cmp.right_label || ''} onChange={(e) => onUpdate({ ...block, content: { ...cmp, right_label: e.target.value } })} placeholder="With Us" className="bg-white" />
              </div>
            </div>
            <div className="space-y-3">
              {rows.map((r, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-start">
                  <div className="col-span-12 md:col-span-5">
                    <Textarea value={r.left || ''} onChange={(e) => updateRow(idx, { left: e.target.value })} placeholder={cmp.left_label || 'Left cell'} rows={2} className="bg-white text-sm" />
                  </div>
                  <div className="col-span-12 md:col-span-6">
                    <Textarea value={r.right || ''} onChange={(e) => updateRow(idx, { right: e.target.value })} placeholder={cmp.right_label || 'Right cell'} rows={2} className="bg-white text-sm" />
                  </div>
                  <div className="col-span-12 md:col-span-1 flex md:justify-end">
                    <Button type="button" variant="ghost" size="icon" className="h-9 w-9 text-red-500 hover:text-red-600 hover:bg-red-50" onClick={() => removeRow(idx)} data-testid={`comparison-row-remove-${idx}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            {rows.length < 8 && (
              <Button type="button" variant="outline" size="sm" onClick={addRow} className="w-full border-dashed" data-testid="comparison-add-row-btn">
                <Plus className="h-4 w-4 mr-1" /> Add Row ({rows.length}/8)
              </Button>
            )}
          </div>
        ) : (
          <div className="rounded-xl overflow-hidden" style={{ backgroundColor: style.secondaryBg, border: `1px solid ${style.accent}30` }}>
            {cmp.headline && (
              <div className="px-5 py-4 text-center font-serif text-xl md:text-2xl tracking-wide" style={{ color: style.text }}>
                {cmp.headline}
              </div>
            )}
            <div className="hidden md:grid grid-cols-2 text-sm font-semibold uppercase tracking-widest">
              <div className="px-5 py-3" style={{ color: style.muted, backgroundColor: 'rgba(0,0,0,0.02)' }}>{cmp.left_label || 'Without Us'}</div>
              <div className="px-5 py-3 border-l-4" style={{ color: style.accent, backgroundColor: `${style.accent}15`, borderLeftColor: style.accent }}>{cmp.right_label || 'With Us'}</div>
            </div>
            <div className="divide-y" style={{ borderColor: `${style.accent}20` }}>
              {rows.map((r, idx) => (
                <div key={idx} className="grid grid-cols-1 md:grid-cols-2" style={{ backgroundColor: idx % 2 === 0 ? 'transparent' : 'rgba(0,0,0,0.02)' }}>
                  <div className="px-5 py-4 flex items-start gap-2 md:border-r" style={{ borderColor: `${style.accent}20` }}>
                    <span className="md:hidden text-xs font-semibold uppercase tracking-widest opacity-60 mr-1" style={{ color: style.muted }}>{cmp.left_label || 'Before'}:</span>
                    <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0 opacity-50" style={{ color: style.muted }} />
                    <span className="text-sm" style={{ color: style.muted }}>{r.left || <span className="opacity-40">—</span>}</span>
                  </div>
                  <div className="px-5 py-4 flex items-start gap-2 border-l-4" style={{ borderLeftColor: style.accent }}>
                    <span className="md:hidden text-xs font-semibold uppercase tracking-widest mr-1" style={{ color: style.accent }}>{cmp.right_label || 'After'}:</span>
                    <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: style.accent }} />
                    <span className="text-sm font-medium" style={{ color: style.text }}>{r.right || <span className="opacity-40">—</span>}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      }

      case 'countdown': {
        const cd = block.content;
        // Format ISO -> input value (YYYY-MM-DDTHH:mm)
        const toInputDate = (iso) => {
          if (!iso) return '';
          try {
            const d = new Date(iso);
            const pad = (n) => String(n).padStart(2, '0');
            return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
          } catch { return ''; }
        };
        return isEditing ? (
          <div className="border border-[#B89B5E]/30 rounded-xl p-5 bg-gradient-to-br from-amber-50/40 to-white space-y-4">
            <div className="flex items-center gap-2 pb-3 border-b border-amber-200/50">
              <Timer className="w-5 h-5 text-[#B89B5E]" />
              <span className="font-medium text-gray-800">Proposal Deadline</span>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Headline</label>
              <Input value={cd.headline || ''} onChange={(e) => onUpdate({ ...block, content: { ...cd, headline: e.target.value } })} placeholder="This offer expires in" className="bg-white" data-testid="countdown-headline" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Expiry Date &amp; Time</label>
              <Input
                type="datetime-local"
                value={toInputDate(cd.expiry_date)}
                onChange={(e) => {
                  const v = e.target.value;
                  onUpdate({ ...block, content: { ...cd, expiry_date: v ? new Date(v).toISOString() : null } });
                }}
                className="bg-white"
                data-testid="countdown-expiry"
              />
              {cd.expiry_date && (
                <p className="text-xs text-gray-500 mt-1">Expires: {new Date(cd.expiry_date).toLocaleString()}</p>
              )}
            </div>
            <div className="flex flex-wrap gap-4">
              {['show_days', 'show_hours', 'show_minutes'].map((key) => (
                <label key={key} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input type="checkbox" checked={cd[key] !== false} onChange={(e) => onUpdate({ ...block, content: { ...cd, [key]: e.target.checked } })} className="rounded border-gray-300 text-[#B89B5E]" />
                  Show {key.replace('show_', '')}
                </label>
              ))}
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Expired Message</label>
              <Textarea value={cd.expired_message || ''} onChange={(e) => onUpdate({ ...block, content: { ...cd, expired_message: e.target.value } })} placeholder="This offer has expired..." rows={2} className="bg-white" />
            </div>
          </div>
        ) : (
          <div className="rounded-xl p-6 md:p-8 text-center" style={{ backgroundColor: style.secondaryBg }}>
            <div className="font-serif text-lg md:text-xl mb-4" style={{ color: style.text }}>{cd.headline || 'This offer expires in'}</div>
            <div className="flex items-center justify-center gap-3 md:gap-6">
              {cd.show_days !== false && (
                <div className="text-center">
                  <div className="font-serif text-3xl md:text-5xl font-bold leading-none" style={{ color: style.accent }}>—</div>
                  <div className="text-[10px] md:text-xs uppercase tracking-widest mt-1" style={{ color: style.muted }}>Days</div>
                </div>
              )}
              {cd.show_hours !== false && (
                <>
                  <div className="font-serif text-2xl md:text-4xl" style={{ color: style.accent }}>:</div>
                  <div className="text-center">
                    <div className="font-serif text-3xl md:text-5xl font-bold leading-none" style={{ color: style.accent }}>—</div>
                    <div className="text-[10px] md:text-xs uppercase tracking-widest mt-1" style={{ color: style.muted }}>Hours</div>
                  </div>
                </>
              )}
              {cd.show_minutes !== false && (
                <>
                  <div className="font-serif text-2xl md:text-4xl" style={{ color: style.accent }}>:</div>
                  <div className="text-center">
                    <div className="font-serif text-3xl md:text-5xl font-bold leading-none" style={{ color: style.accent }}>—</div>
                    <div className="text-[10px] md:text-xs uppercase tracking-widest mt-1" style={{ color: style.muted }}>Minutes</div>
                  </div>
                </>
              )}
            </div>
            {!cd.expiry_date && (
              <p className="text-xs italic mt-4" style={{ color: style.muted }}>(Set an expiry date in the editor to start the timer)</p>
            )}
          </div>
        );
      }

      case 'pricing': {
        const p = block.content;
        const tiers = Array.isArray(p.tiers) ? p.tiers : [];
        const updateTier = (idx, patch) => {
          const next = [...tiers];
          next[idx] = { ...next[idx], ...patch };
          onUpdate({ ...block, content: { ...p, tiers: next } });
        };
        const removeTier = (idx) => onUpdate({ ...block, content: { ...p, tiers: tiers.filter((_, i) => i !== idx) } });
        const addTier = () => {
          if (tiers.length >= 3) return;
          onUpdate({ ...block, content: { ...p, tiers: [...tiers, { name: 'New Tier', price: '', period: 'per month', description: '', features: [], is_recommended: false, cta_text: 'Get Started', cta_url: '' }] } });
        };
        const updateFeature = (tierIdx, featIdx, value) => {
          const features = [...(tiers[tierIdx].features || [])];
          features[featIdx] = value;
          updateTier(tierIdx, { features });
        };
        const addFeature = (tierIdx) => updateTier(tierIdx, { features: [...(tiers[tierIdx].features || []), ''] });
        const removeFeature = (tierIdx, featIdx) => updateTier(tierIdx, { features: (tiers[tierIdx].features || []).filter((_, i) => i !== featIdx) });
        return isEditing ? (
          <div className="border border-[#B89B5E]/30 rounded-xl p-5 bg-gradient-to-br from-amber-50/40 to-white space-y-4">
            <div className="flex items-center gap-2 pb-3 border-b border-amber-200/50">
              <DollarSign className="w-5 h-5 text-[#B89B5E]" />
              <span className="font-medium text-gray-800">Pricing Options</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="md:col-span-2">
                <label className="text-xs font-medium text-gray-600 mb-1 block">Headline</label>
                <Input value={p.headline || ''} onChange={(e) => onUpdate({ ...block, content: { ...p, headline: e.target.value } })} className="bg-white" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Currency</label>
                <Input value={p.currency_symbol || '$'} maxLength={3} onChange={(e) => onUpdate({ ...block, content: { ...p, currency_symbol: e.target.value } })} className="bg-white" />
              </div>
            </div>
            <div className="space-y-4">
              {tiers.map((tier, idx) => (
                <div key={idx} className={`border rounded-lg p-4 space-y-3 ${tier.is_recommended ? 'border-[#B89B5E] bg-amber-50/30' : 'border-gray-200 bg-white'}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Tier {idx + 1}{tier.is_recommended ? ' · Recommended' : ''}</span>
                    <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:bg-red-50" onClick={() => removeTier(idx)} data-testid={`pricing-tier-remove-${idx}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <label className="text-xs font-medium text-gray-600 mb-1 block">Tier Name</label>
                      <Input value={tier.name || ''} onChange={(e) => updateTier(idx, { name: e.target.value })} className="bg-white" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-600 mb-1 block">Price</label>
                      <Input value={tier.price || ''} onChange={(e) => updateTier(idx, { price: e.target.value })} placeholder="299" className="bg-white" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-600 mb-1 block">Period</label>
                      <Input value={tier.period || ''} onChange={(e) => updateTier(idx, { period: e.target.value })} placeholder="per month" className="bg-white" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Description</label>
                    <Textarea value={tier.description || ''} onChange={(e) => updateTier(idx, { description: e.target.value })} rows={2} className="bg-white" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Features</label>
                    <div className="space-y-2">
                      {(tier.features || []).map((f, fIdx) => (
                        <div key={fIdx} className="flex gap-2">
                          <Input value={f} onChange={(e) => updateFeature(idx, fIdx, e.target.value)} className="bg-white text-sm flex-1" />
                          <Button type="button" variant="ghost" size="icon" className="h-9 w-9 text-red-500" onClick={() => removeFeature(idx, fIdx)}><Trash2 className="h-4 w-4" /></Button>
                        </div>
                      ))}
                      <Button type="button" variant="outline" size="sm" onClick={() => addFeature(idx)} className="w-full border-dashed text-xs"><Plus className="h-3 w-3 mr-1" /> Add Feature</Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-gray-600 mb-1 block">CTA Text</label>
                      <Input value={tier.cta_text || ''} onChange={(e) => updateTier(idx, { cta_text: e.target.value })} className="bg-white" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-600 mb-1 block">CTA URL</label>
                      <Input value={tier.cta_url || ''} onChange={(e) => updateTier(idx, { cta_url: e.target.value })} placeholder="https://..." className="bg-white" />
                    </div>
                  </div>
                  <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer pt-1">
                    <input type="checkbox" checked={!!tier.is_recommended} onChange={(e) => {
                      // only one tier can be recommended
                      const checked = e.target.checked;
                      const next = tiers.map((t, i) => ({ ...t, is_recommended: i === idx ? checked : false }));
                      onUpdate({ ...block, content: { ...p, tiers: next } });
                    }} className="rounded border-gray-300 text-[#B89B5E]" />
                    Mark as Recommended
                  </label>
                </div>
              ))}
            </div>
            {tiers.length < 3 && (
              <Button type="button" variant="outline" size="sm" onClick={addTier} className="w-full border-dashed" data-testid="pricing-add-tier-btn"><Plus className="h-4 w-4 mr-1" /> Add Tier ({tiers.length}/3)</Button>
            )}
          </div>
        ) : (
          <div className="rounded-xl p-5 md:p-8" style={{ backgroundColor: style.secondaryBg }}>
            {p.headline && <h3 className="font-serif text-xl md:text-2xl mb-6 text-center" style={{ color: style.text }}>{p.headline}</h3>}
            <div className={`grid grid-cols-1 ${tiers.length >= 2 ? 'md:grid-cols-2' : ''} ${tiers.length === 3 ? 'md:grid-cols-3' : ''} gap-4`}>
              {tiers.map((tier, idx) => {
                const missingPrice = !tier.price || !String(tier.price).trim();
                const showPlaceholder = !!p.needs_pricing && missingPrice;
                return (
                <div key={idx} className={`relative rounded-xl border p-5 flex flex-col ${tier.is_recommended ? 'md:scale-105' : ''}`} style={{ borderColor: tier.is_recommended ? style.accent : `${style.accent}30`, backgroundColor: tier.is_recommended ? `${style.accent}10` : 'white' }}>
                  {tier.is_recommended && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest text-white" style={{ backgroundColor: style.accent }}>
                      <Star className="w-3 h-3 inline mr-1" /> Recommended
                    </div>
                  )}
                  <div className="text-xs uppercase tracking-widest mb-2" style={{ color: style.accent }}>{tier.name || 'Tier'}</div>
                  {showPlaceholder ? (
                    <div
                      className="rounded-md border-2 border-dashed px-3 py-3 mb-3 text-center"
                      style={{ borderColor: '#D97706', backgroundColor: '#FFFBEB', color: '#B45309' }}
                      data-testid={`pricing-needs-input-editor-${idx}`}
                    >
                      <div className="text-[10px] font-semibold uppercase tracking-widest mb-0.5">Pricing needed</div>
                      <div className="text-xs font-medium italic">{p.pricing_placeholder_text || 'Add your pricing — click to edit'}</div>
                    </div>
                  ) : (
                    <div className="flex items-baseline gap-1 mb-2">
                      <span className="font-serif text-3xl md:text-4xl font-bold leading-none" style={{ color: style.text }}>{p.currency_symbol || ''}{tier.price || '—'}</span>
                    </div>
                  )}
                  {tier.period && !showPlaceholder && <div className="text-xs mb-3" style={{ color: style.muted }}>{tier.period}</div>}
                  {tier.description && <p className="italic text-sm mb-4" style={{ color: style.muted }}>{tier.description}</p>}
                  <ul className="space-y-2 mb-5 flex-1">
                    {(tier.features || []).map((f, fIdx) => (
                      <li key={fIdx} className="flex items-start gap-2 text-sm" style={{ color: style.text }}>
                        <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: style.accent }} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  {(tier.cta_text || tier.cta_url) && (
                    <a href={normalizeExternalUrl(tier.cta_url)} target={tier.cta_url ? '_blank' : undefined} rel="noopener noreferrer" className="block text-center px-4 py-2.5 rounded-md font-semibold text-sm transition" style={{ backgroundColor: tier.is_recommended ? style.accent : 'transparent', color: tier.is_recommended ? '#FFFFFF' : style.accent, border: `1.5px solid ${style.accent}` }}>
                      {tier.cta_text || 'Get Started'}
                    </a>
                  )}
                </div>
                );
              })}
            </div>
          </div>
        );
      }

      case 'timeline': {
        const tl = block.content;
        const items = Array.isArray(tl.items) ? tl.items : [];
        const updateItem = (idx, patch) => {
          const next = [...items];
          next[idx] = { ...next[idx], ...patch };
          onUpdate({ ...block, content: { ...tl, items: next } });
        };
        const removeItem = (idx) => onUpdate({ ...block, content: { ...tl, items: items.filter((_, i) => i !== idx) } });
        const addItem = () => {
          if (items.length >= 6) return;
          onUpdate({ ...block, content: { ...tl, items: [...items, { period: '', title: '', description: '' }] } });
        };
        return isEditing ? (
          <div className="border border-[#B89B5E]/30 rounded-xl p-5 bg-gradient-to-br from-amber-50/40 to-white space-y-4">
            <div className="flex items-center gap-2 pb-3 border-b border-amber-200/50">
              <GitCommit className="w-5 h-5 text-[#B89B5E]" />
              <span className="font-medium text-gray-800">Project Timeline</span>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Headline</label>
              <Input value={tl.headline || ''} onChange={(e) => onUpdate({ ...block, content: { ...tl, headline: e.target.value } })} className="bg-white" />
            </div>
            <div className="space-y-3">
              {items.map((it, idx) => (
                <div key={idx} className="border border-gray-200 rounded-lg p-3 bg-white space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-gray-500 uppercase">Milestone {idx + 1}</span>
                    <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:bg-red-50" onClick={() => removeItem(idx)} data-testid={`timeline-item-remove-${idx}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    <Input value={it.period || ''} onChange={(e) => updateItem(idx, { period: e.target.value })} placeholder="Week 1 / Month 1 / Q2..." className="md:col-span-1" />
                    <Input value={it.title || ''} onChange={(e) => updateItem(idx, { title: e.target.value })} placeholder="Discovery & Setup" className="md:col-span-2" />
                  </div>
                  <Textarea value={it.description || ''} onChange={(e) => updateItem(idx, { description: e.target.value })} placeholder="Brief description of what happens at this milestone..." rows={2} />
                </div>
              ))}
            </div>
            {items.length < 6 && (
              <Button type="button" variant="outline" size="sm" onClick={addItem} className="w-full border-dashed" data-testid="timeline-add-item-btn"><Plus className="h-4 w-4 mr-1" /> Add Milestone ({items.length}/6)</Button>
            )}
          </div>
        ) : (
          <div className="rounded-xl p-5 md:p-8" style={{ backgroundColor: style.secondaryBg }}>
            {tl.headline && <h3 className="font-serif text-xl md:text-2xl mb-6 md:mb-8 text-center" style={{ color: style.text }}>{tl.headline}</h3>}
            <div className="relative pl-8 md:pl-12">
              <div className="absolute left-2 md:left-3 top-2 bottom-2 w-0.5" style={{ backgroundColor: `${style.accent}50` }} />
              <div className="space-y-6 md:space-y-8">
                {items.map((it, idx) => (
                  <div key={idx} className="relative">
                    <div className="absolute -left-7 md:-left-10 top-1.5 w-3 h-3 md:w-4 md:h-4 rounded-full" style={{ backgroundColor: style.accent, outline: `3px solid ${style.secondaryBg}` }} />
                    {it.period && <div className="text-[10px] md:text-xs uppercase tracking-widest mb-1" style={{ color: style.accent }}>{it.period}</div>}
                    {it.title && <div className="font-serif text-base md:text-xl font-semibold mb-1" style={{ color: style.text }}>{it.title}</div>}
                    {it.description && <p className="text-sm" style={{ color: style.muted }}>{it.description}</p>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      }

      case 'faq': {
        const f = block.content;
        const items = Array.isArray(f.items) ? f.items : [];
        const updateItem = (idx, patch) => {
          const next = [...items];
          next[idx] = { ...next[idx], ...patch };
          onUpdate({ ...block, content: { ...f, items: next } });
        };
        const removeItem = (idx) => onUpdate({ ...block, content: { ...f, items: items.filter((_, i) => i !== idx) } });
        const addItem = () => {
          if (items.length >= 8) return;
          onUpdate({ ...block, content: { ...f, items: [...items, { question: '', answer: '' }] } });
        };
        return isEditing ? (
          <div className="border border-[#B89B5E]/30 rounded-xl p-5 bg-gradient-to-br from-amber-50/40 to-white space-y-4">
            <div className="flex items-center gap-2 pb-3 border-b border-amber-200/50">
              <HelpCircle className="w-5 h-5 text-[#B89B5E]" />
              <span className="font-medium text-gray-800">FAQ</span>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Headline</label>
              <Input value={f.headline || ''} onChange={(e) => onUpdate({ ...block, content: { ...f, headline: e.target.value } })} className="bg-white" />
            </div>
            <div className="space-y-3">
              {items.map((it, idx) => (
                <div key={idx} className="border border-gray-200 rounded-lg p-3 bg-white space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-gray-500 uppercase">Question {idx + 1}</span>
                    <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:bg-red-50" onClick={() => removeItem(idx)} data-testid={`faq-item-remove-${idx}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                  <Input value={it.question || ''} onChange={(e) => updateItem(idx, { question: e.target.value })} placeholder="Question..." />
                  <Textarea value={it.answer || ''} onChange={(e) => updateItem(idx, { answer: e.target.value })} placeholder="Answer..." rows={3} />
                </div>
              ))}
            </div>
            {items.length < 8 && (
              <Button type="button" variant="outline" size="sm" onClick={addItem} className="w-full border-dashed" data-testid="faq-add-item-btn"><Plus className="h-4 w-4 mr-1" /> Add Question ({items.length}/8)</Button>
            )}
          </div>
        ) : (
          <div className="rounded-xl p-5 md:p-8" style={{ backgroundColor: style.secondaryBg }}>
            {f.headline && <h3 className="font-serif text-xl md:text-2xl mb-5 text-center" style={{ color: style.text }}>{f.headline}</h3>}
            <div className="divide-y" style={{ borderColor: `${style.accent}30` }}>
              {items.map((it, idx) => (
                <div key={idx} className="py-3">
                  <div className="flex items-start justify-between gap-3 font-semibold mb-2" style={{ color: style.text }}>
                    <span>{it.question || <span className="opacity-40">Question</span>}</span>
                    <ChevronDown className="w-4 h-4 mt-1 flex-shrink-0" style={{ color: style.accent }} />
                  </div>
                  <p className="text-sm" style={{ color: style.muted }}>{it.answer || <span className="opacity-40">(answer)</span>}</p>
                </div>
              ))}
            </div>
          </div>
        );
      }

      case 'roi-calculator': {
        const r = block.content;
        const inputs = Array.isArray(r.inputs) ? r.inputs : [];
        const outputs = Array.isArray(r.outputs) ? r.outputs : [];
        const updateInput = (idx, patch) => {
          const next = [...inputs]; next[idx] = { ...next[idx], ...patch };
          onUpdate({ ...block, content: { ...r, inputs: next } });
        };
        const updateOutput = (idx, patch) => {
          const next = [...outputs]; next[idx] = { ...next[idx], ...patch };
          onUpdate({ ...block, content: { ...r, outputs: next } });
        };
        const removeInput = (idx) => onUpdate({ ...block, content: { ...r, inputs: inputs.filter((_, i) => i !== idx) } });
        const removeOutput = (idx) => onUpdate({ ...block, content: { ...r, outputs: outputs.filter((_, i) => i !== idx) } });
        const addInput = () => onUpdate({ ...block, content: { ...r, inputs: [...inputs, { id: `input_${inputs.length + 1}`, label: 'New Input', default_value: 0, min: 0, max: 100, prefix: '', suffix: '' }] } });
        const addOutput = () => onUpdate({ ...block, content: { ...r, outputs: [...outputs, { id: `output_${outputs.length + 1}`, label: 'New Output', formula: '', prefix: '', suffix: '', highlight: false }] } });
        return isEditing ? (
          <div className="border border-[#B89B5E]/30 rounded-xl p-5 bg-gradient-to-br from-amber-50/40 to-white space-y-4">
            <div className="flex items-center gap-2 pb-3 border-b border-amber-200/50">
              <Calculator className="w-5 h-5 text-[#B89B5E]" />
              <span className="font-medium text-gray-800">ROI Calculator</span>
            </div>
            <Input value={r.headline || ''} onChange={(e) => onUpdate({ ...block, content: { ...r, headline: e.target.value } })} placeholder="Headline" className="bg-white" />
            <Input value={r.subheadline || ''} onChange={(e) => onUpdate({ ...block, content: { ...r, subheadline: e.target.value } })} placeholder="Subheadline" className="bg-white" />
            <div className="space-y-2">
              <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Inputs</div>
              {inputs.map((inp, idx) => (
                <div key={idx} className="border rounded-lg p-3 bg-white space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">Input {idx + 1}</span>
                    <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => removeInput(idx)}><Trash2 className="h-3 w-3" /></Button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Input value={inp.id || ''} onChange={(e) => updateInput(idx, { id: e.target.value.replace(/[^a-z0-9_]/gi, '_').toLowerCase() })} placeholder="id (variable name)" className="text-xs font-mono" />
                    <Input value={inp.label || ''} onChange={(e) => updateInput(idx, { label: e.target.value })} placeholder="Label" />
                  </div>
                  <div className="grid grid-cols-4 gap-2">
                    <Input type="number" value={inp.default_value ?? ''} onChange={(e) => updateInput(idx, { default_value: Number(e.target.value) })} placeholder="default" className="text-xs" />
                    <Input type="number" value={inp.min ?? ''} onChange={(e) => updateInput(idx, { min: Number(e.target.value) })} placeholder="min" className="text-xs" />
                    <Input type="number" value={inp.max ?? ''} onChange={(e) => updateInput(idx, { max: Number(e.target.value) })} placeholder="max" className="text-xs" />
                    <div className="flex gap-1">
                      <Input value={inp.prefix || ''} onChange={(e) => updateInput(idx, { prefix: e.target.value })} placeholder="$" className="text-xs w-1/2" />
                      <Input value={inp.suffix || ''} onChange={(e) => updateInput(idx, { suffix: e.target.value })} placeholder="%" className="text-xs w-1/2" />
                    </div>
                  </div>
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={addInput} className="w-full border-dashed text-xs"><Plus className="h-3 w-3 mr-1" /> Add Input</Button>
            </div>
            <div className="space-y-2">
              <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Outputs</div>
              {outputs.map((out, idx) => (
                <div key={idx} className={`border rounded-lg p-3 space-y-2 ${out.highlight ? 'bg-amber-50/50 border-[#B89B5E]/40' : 'bg-white'}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">Output {idx + 1}{out.highlight ? ' · Highlighted' : ''}</span>
                    <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => removeOutput(idx)}><Trash2 className="h-3 w-3" /></Button>
                  </div>
                  <Input value={out.label || ''} onChange={(e) => updateOutput(idx, { label: e.target.value })} placeholder="Label" />
                  <Input value={out.formula || ''} onChange={(e) => updateOutput(idx, { formula: e.target.value })} placeholder="team_size * avg_deal * 0.18" className="font-mono text-xs" />
                  <div className="flex gap-2">
                    <Input value={out.prefix || ''} onChange={(e) => updateOutput(idx, { prefix: e.target.value })} placeholder="prefix ($)" className="text-xs" />
                    <Input value={out.suffix || ''} onChange={(e) => updateOutput(idx, { suffix: e.target.value })} placeholder="suffix" className="text-xs" />
                  </div>
                  <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
                    <input type="checkbox" checked={!!out.highlight} onChange={(e) => updateOutput(idx, { highlight: e.target.checked })} className="rounded border-gray-300 text-[#B89B5E]" />
                    Highlight this output
                  </label>
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={addOutput} className="w-full border-dashed text-xs"><Plus className="h-3 w-3 mr-1" /> Add Output</Button>
            </div>
            <div className="text-[11px] italic text-gray-500 bg-amber-50/40 px-3 py-2 rounded">
              Formulas use input field IDs as variables. Example: <span className="font-mono">team_size * avg_deal * 0.18</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input value={r.cta_text || ''} onChange={(e) => onUpdate({ ...block, content: { ...r, cta_text: e.target.value } })} placeholder="CTA Text" />
              <Input value={r.cta_url || ''} onChange={(e) => onUpdate({ ...block, content: { ...r, cta_url: e.target.value } })} placeholder="CTA URL" />
            </div>
          </div>
        ) : null;
      }

      case 'booking': {
        const b = block.content;
        return isEditing ? (
          <div className="border border-[#B89B5E]/30 rounded-xl p-5 bg-gradient-to-br from-amber-50/40 to-white space-y-4">
            <div className="flex items-center gap-2 pb-3 border-b border-amber-200/50">
              <CalendarCheck className="w-5 h-5 text-[#B89B5E]" />
              <span className="font-medium text-gray-800">Book a Meeting</span>
            </div>
            <Input value={b.headline || ''} onChange={(e) => onUpdate({ ...block, content: { ...b, headline: e.target.value } })} placeholder="Headline" className="bg-white" />
            <Textarea value={b.subheadline || ''} onChange={(e) => onUpdate({ ...block, content: { ...b, subheadline: e.target.value } })} placeholder="Subheadline" rows={2} className="bg-white" />
            <Input value={b.booking_url || ''} onChange={(e) => onUpdate({ ...block, content: { ...b, booking_url: e.target.value } })} placeholder="https://calendly.com/you/intro-call" className="bg-white font-mono text-sm" data-testid="booking-url" />
            <Input value={b.button_text || ''} onChange={(e) => onUpdate({ ...block, content: { ...b, button_text: e.target.value } })} placeholder="Schedule Your Call" className="bg-white" />
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input type="checkbox" checked={!!b.show_inline} onChange={(e) => onUpdate({ ...block, content: { ...b, show_inline: e.target.checked } })} className="rounded border-gray-300 text-[#B89B5E]" />
              Show inline embed instead of button
            </label>
            <p className="text-[11px] italic text-gray-500">Inline embed works with Calendly and Cal.com links. Other URLs open in new tab.</p>
          </div>
        ) : null;
      }

      case 'lead-form': {
        const lf = block.content;
        const fields = Array.isArray(lf.fields) ? lf.fields : [];
        const updateField = (idx, patch) => {
          const next = [...fields]; next[idx] = { ...next[idx], ...patch };
          onUpdate({ ...block, content: { ...lf, fields: next } });
        };
        const removeField = (idx) => onUpdate({ ...block, content: { ...lf, fields: fields.filter((_, i) => i !== idx) } });
        const addField = () => {
          if (fields.length >= 6) return;
          onUpdate({ ...block, content: { ...lf, fields: [...fields, { id: `field_${fields.length + 1}`, label: 'New Field', type: 'text', required: false }] } });
        };
        return isEditing ? (
          <div className="border border-[#B89B5E]/30 rounded-xl p-5 bg-gradient-to-br from-amber-50/40 to-white space-y-4">
            <div className="flex items-center gap-2 pb-3 border-b border-amber-200/50">
              <ClipboardList className="w-5 h-5 text-[#B89B5E]" />
              <span className="font-medium text-gray-800">Request Form</span>
            </div>
            <Input value={lf.headline || ''} onChange={(e) => onUpdate({ ...block, content: { ...lf, headline: e.target.value } })} placeholder="Headline" className="bg-white" />
            <Textarea value={lf.subheadline || ''} onChange={(e) => onUpdate({ ...block, content: { ...lf, subheadline: e.target.value } })} placeholder="Subheadline" rows={2} className="bg-white" />
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Notify Email (where submissions are sent)</label>
              <Input type="email" value={lf.notify_email || ''} onChange={(e) => onUpdate({ ...block, content: { ...lf, notify_email: e.target.value } })} placeholder="you@company.com" className="bg-white" />
            </div>
            <div className="space-y-2">
              <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Fields ({fields.length}/6)</div>
              {fields.map((fld, idx) => (
                <div key={idx} className="border rounded-lg p-3 bg-white space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">Field {idx + 1}</span>
                    <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => removeField(idx)}><Trash2 className="h-3 w-3" /></Button>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <Input value={fld.id || ''} onChange={(e) => updateField(idx, { id: e.target.value.replace(/[^a-z0-9_]/gi, '_').toLowerCase() })} placeholder="id" className="text-xs font-mono" />
                    <Input value={fld.label || ''} onChange={(e) => updateField(idx, { label: e.target.value })} placeholder="Label" className="col-span-2" />
                  </div>
                  <div className="flex items-center gap-3">
                    <select value={fld.type || 'text'} onChange={(e) => updateField(idx, { type: e.target.value })} className="text-xs border rounded px-2 py-1.5 bg-white flex-1">
                      <option value="text">text</option>
                      <option value="email">email</option>
                      <option value="tel">tel</option>
                      <option value="textarea">textarea</option>
                      <option value="select">select</option>
                    </select>
                    <label className="flex items-center gap-1 text-xs text-gray-600 cursor-pointer">
                      <input type="checkbox" checked={!!fld.required} onChange={(e) => updateField(idx, { required: e.target.checked })} className="rounded border-gray-300 text-[#B89B5E]" />
                      Required
                    </label>
                  </div>
                </div>
              ))}
              {fields.length < 6 && (
                <Button type="button" variant="outline" size="sm" onClick={addField} className="w-full border-dashed text-xs"><Plus className="h-3 w-3 mr-1" /> Add Field</Button>
              )}
            </div>
            <Input value={lf.submit_text || ''} onChange={(e) => onUpdate({ ...block, content: { ...lf, submit_text: e.target.value } })} placeholder="Submit button text" className="bg-white" />
            <Textarea value={lf.success_message || ''} onChange={(e) => onUpdate({ ...block, content: { ...lf, success_message: e.target.value } })} placeholder="Success message after submission" rows={2} className="bg-white" />
          </div>
        ) : null;
      }

      case 'video-testimonial': {
        const vt = block.content;
        // Auto-extract YouTube thumbnail
        const ytIdMatch = vt.video_url ? (vt.video_url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/))([\w-]{11})/) || [])[1] : null;
        const autoThumb = ytIdMatch ? `https://img.youtube.com/vi/${ytIdMatch}/maxresdefault.jpg` : '';
        const effectiveThumb = vt.thumbnail_url || autoThumb;
        return isEditing ? (
          <div className="border border-[#B89B5E]/30 rounded-xl p-5 bg-gradient-to-br from-amber-50/40 to-white space-y-4">
            <div className="flex items-center gap-2 pb-3 border-b border-amber-200/50">
              <PlayCircle className="w-5 h-5 text-[#B89B5E]" />
              <span className="font-medium text-gray-800">Video Testimonial</span>
            </div>
            <Input value={vt.headline || ''} onChange={(e) => onUpdate({ ...block, content: { ...vt, headline: e.target.value } })} placeholder="Headline" className="bg-white" />
            <Input value={vt.video_url || ''} onChange={(e) => onUpdate({ ...block, content: { ...vt, video_url: e.target.value } })} placeholder="https://www.youtube.com/watch?v=..." className="bg-white font-mono text-sm" data-testid="video-testimonial-url" />
            {ytIdMatch && (
              <div className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-3 py-1.5">
                YouTube ID detected: <span className="font-mono">{ytIdMatch}</span> · thumbnail auto-extracted
              </div>
            )}
            <div>
              <label className="text-xs font-medium text-gray-600 mb-2 block">Custom Thumbnail (optional — overrides auto-extract)</label>
              <ImageUploader
                value={vt.thumbnail_url}
                onChange={(url) => onUpdate({ ...block, content: { ...vt, thumbnail_url: url } })}
                onRemove={() => onUpdate({ ...block, content: { ...vt, thumbnail_url: '' } })}
                placeholder="Upload custom thumbnail"
              />
              {effectiveThumb && (
                <div className="mt-2 border rounded overflow-hidden bg-black" style={{ aspectRatio: '16/9' }}>
                  <img src={effectiveThumb} alt="thumbnail preview" className="w-full h-full object-cover" />
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input value={vt.attribution_name || ''} onChange={(e) => onUpdate({ ...block, content: { ...vt, attribution_name: e.target.value } })} placeholder="Attribution name" />
              <Input value={vt.attribution_title || ''} onChange={(e) => onUpdate({ ...block, content: { ...vt, attribution_title: e.target.value } })} placeholder="Attribution title" />
            </div>
            <Input value={vt.attribution_company || ''} onChange={(e) => onUpdate({ ...block, content: { ...vt, attribution_company: e.target.value } })} placeholder="Company" />
            <Textarea value={vt.caption || ''} onChange={(e) => onUpdate({ ...block, content: { ...vt, caption: e.target.value } })} placeholder="Caption (optional)" rows={2} />
          </div>
        ) : null;
      }

      default:
        // Universal Builder: delegate unknown types to the additive registry
        // (editor renderer). Falls back to null when not registered.
        return renderRegistryBlockEditor({ block, style, isEditing, onUpdate, onDelete, onDuplicate, onMoveUp, onMoveDown, isFirst, isLast, pages });
    }
  };

  return (
    <div 
      className="group relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Mobile Block Controls Bar - Always visible on touch devices */}
      {isEditing && (
        <div className="md:hidden flex items-center justify-between gap-2 mb-2 p-2 bg-white rounded-lg border shadow-sm">
          <div className="flex items-center gap-1">
            <span className="text-xs text-gray-400 capitalize">{block.type}</span>
          </div>
          <div className="flex items-center gap-1">
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8" 
              disabled={isFirst} 
              onClick={onMoveUp}
            >
              <MoveUp className="w-4 h-4" />
            </Button>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8" 
              disabled={isLast} 
              onClick={onMoveDown}
            >
              <MoveDown className="w-4 h-4" />
            </Button>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8" 
              onClick={onDuplicate}
            >
              <Copy className="w-4 h-4" />
            </Button>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50" 
              onClick={onDelete}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Desktop Block Controls - Left Side (Appear on Hover) */}
      {isEditing && (
        <div className={`hidden md:flex absolute -left-14 top-1/2 -translate-y-1/2 flex-col gap-1 transition-opacity duration-200 ${isHovered ? 'opacity-100' : 'opacity-0'}`}>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8 bg-white shadow-sm border" disabled={isFirst} onClick={onMoveUp}>
                  <MoveUp className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="left">Move up</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8 bg-white shadow-sm border" disabled={isLast} onClick={onMoveDown}>
                  <MoveDown className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="left">Move down</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      )}

      {/* Block Content */}
      <div className={`relative rounded-xl transition-all duration-200 ${isEditing && isHovered ? 'ring-2 ring-[#B89B5E]/30 bg-white/50' : ''}`}>
        {renderBlockContent()}
      </div>

      {/* Desktop Right Side Actions (Appear on Hover) */}
      {isEditing && (
        <div className={`hidden md:flex absolute -right-12 top-1/2 -translate-y-1/2 flex-col gap-1 transition-opacity duration-200 ${isHovered ? 'opacity-100' : 'opacity-0'}`}>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8 bg-white shadow-sm border" onClick={onDuplicate}>
                  <Copy className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">Duplicate</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8 bg-white shadow-sm border text-red-500 hover:text-red-600 hover:bg-red-50" onClick={onDelete}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">Delete</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      )}
    </div>
  );
}

// ============================================
// PAGE THUMBNAIL COMPONENT
// ============================================
function PageThumbnail({ page, index, isActive, onClick, onRename, onDuplicate, onDelete, canDelete, style }) {
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(page.name || `Page ${index + 1}`);

  const handleRename = () => {
    onRename(name);
    setIsEditing(false);
  };

  return (
    <div className={`group relative rounded-xl transition-all duration-200 ${isActive ? 'ring-2 ring-[#B89B5E] shadow-lg' : 'hover:shadow-md'}`}>
      {/* Thumbnail Preview */}
      <button
        onClick={onClick}
        className="w-full aspect-[4/3] rounded-lg overflow-hidden"
        style={{ backgroundColor: style.background }}
      >
        <div className="w-full h-full flex items-center justify-center p-2">
          <div className="text-center">
            <Layers className="w-6 h-6 mx-auto mb-1" style={{ color: style.muted }} />
            <span className="text-xs font-medium" style={{ color: style.text }}>
              {page.blocks?.length || 0} blocks
            </span>
          </div>
        </div>
      </button>

      {/* Page Name */}
      <div className="mt-2 px-1">
        {isEditing ? (
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={handleRename}
            onKeyDown={(e) => e.key === 'Enter' && handleRename()}
            className="h-7 text-xs text-center"
            autoFocus
          />
        ) : (
          <p className="text-xs text-center text-gray-600 truncate cursor-pointer hover:text-[#B89B5E]" onClick={() => setIsEditing(true)}>
            {page.name || `Page ${index + 1}`}
          </p>
        )}
      </div>

      {/* Hover Actions */}
      <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-6 w-6 bg-white/90 shadow-sm">
              <MoreHorizontal className="w-3 h-3" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-36">
            <DropdownMenuItem onClick={() => setIsEditing(true)}>
              <Edit3 className="w-3 h-3 mr-2" /> Rename
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onDuplicate}>
              <Copy className="w-3 h-3 mr-2" /> Duplicate
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onDelete} disabled={!canDelete} className="text-red-600 focus:text-red-600">
              <Trash2 className="w-3 h-3 mr-2" /> Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Active Indicator */}
      {isActive && (
        <div className="absolute -left-1 top-1/2 -translate-y-1/2 w-1 h-8 bg-[#B89B5E] rounded-full" />
      )}
    </div>
  );
}

// ============================================
// ADD BLOCK MODAL
// ============================================
function AddBlockModal({ open, onClose, onSelect }) {
  // Map icon names to actual icon components
  const iconMap = {
    Type,
    Image,
    Film,
    Play,
    Square,
    Minus,
    MousePointer2,
    SlidersHorizontal,
    MessageSquare,
    TrendingUp,
    UserCircle,
    Columns,
    Timer,
    DollarSign,
    GitCommit,
    HelpCircle,
    Calculator,
    CalendarCheck,
    ClipboardList,
    PlayCircle,
    Grid3x3,
    PenTool,
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-xl font-light tracking-wide">Add Content Block</DialogTitle>
          <DialogDescription>Choose a content type to add to your deck</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 py-6">
          {BLOCK_TYPES.map((blockType) => {
            const IconComponent = iconMap[blockType.icon] || Type;
            return (
              <button
                key={blockType.type}
                onClick={() => { onSelect(blockType.type); onClose(); }}
                className="group p-6 rounded-xl border-2 border-gray-100 hover:border-[#B89B5E] hover:bg-[#B89B5E]/5 transition-all duration-200 text-left"
              >
                <div className="w-12 h-12 rounded-xl bg-[#B89B5E]/10 flex items-center justify-center mb-4 group-hover:bg-[#B89B5E]/20 transition-colors">
                  <IconComponent className="w-6 h-6 text-[#B89B5E]" />
                </div>
                <h3 className="font-medium text-gray-900 mb-1">{blockType.label}</h3>
                <p className="text-sm text-gray-500">{blockType.description}</p>
              </button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============================================
// MAIN EDITOR COMPONENT
// ============================================
export default function VisionDeckEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  
  const [deck, setDeck] = useState(null);
  const [pages, setPages] = useState([]);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showBlockPicker, setShowBlockPicker] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [pageToDelete, setPageToDelete] = useState(null);
  const [undoStack, setUndoStack] = useState([]);
  const [showSidebar, setShowSidebar] = useState(true);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastSavedPages, setLastSavedPages] = useState(null);
  const autoSaveTimeoutRef = useRef(null);

  useEffect(() => {
    fetchDeck();
  }, [id]);

  // Auto-save when pages change (debounced)
  useEffect(() => {
    if (!deck || loading) return;
    
    // Check if pages actually changed from last saved state
    const pagesJson = JSON.stringify(pages);
    const lastSavedJson = lastSavedPages ? JSON.stringify(lastSavedPages) : null;
    
    if (lastSavedJson && pagesJson !== lastSavedJson) {
      setHasUnsavedChanges(true);
      
      // Clear existing timeout
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
      
      // Set new auto-save timeout (2 seconds after last change)
      autoSaveTimeoutRef.current = setTimeout(() => {
        autoSave();
      }, 2000);
    }
    
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [pages, deck, loading, lastSavedPages]);

  // Warn about unsaved changes when leaving
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const autoSave = async () => {
    if (!deck) return;
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/vision-decks/${id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...deck, pages })
      });
      if (response.ok) {
        setLastSavedPages(JSON.parse(JSON.stringify(pages)));
        setHasUnsavedChanges(false);
        // Subtle auto-save notification
        toast.success('Auto-saved', { duration: 1500 });
      }
    } catch (error) {
      console.error('Auto-save failed:', error);
    } finally {
      setSaving(false);
    }
  };

  const fetchDeck = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Please log in to access this deck');
        navigate('/login');
        return;
      }
      const response = await fetch(`${API}/api/vision-decks/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDeck(data);
        const initialPages = data.pages?.length ? data.pages : [{ id: '1', name: 'Cover', blocks: [] }];
        setPages(initialPages);
        setLastSavedPages(JSON.parse(JSON.stringify(initialPages)));
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Failed to load deck:', response.status, errorData);
        toast.error(errorData.detail || 'Failed to load deck');
        navigate('/vision-decks');
      }
    } catch (error) {
      console.error('Error loading deck:', error);
      toast.error('Error loading deck - please check your connection');
    } finally {
      setLoading(false);
    }
  };

  const [showPublishSuccess, setShowPublishSuccess] = useState(false);
  const [publishedData, setPublishedData] = useState(null);

  const handleSave = async () => {
    // Clear any pending auto-save
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/vision-decks/${id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...deck, pages })
      });
      if (response.ok) {
        setLastSavedPages(JSON.parse(JSON.stringify(pages)));
        setHasUnsavedChanges(false);
        toast.success('Saved successfully');
      } else {
        toast.error('Failed to save');
      }
    } catch (error) {
      toast.error('Error saving deck');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      // Save first
      await fetch(`${API}/api/vision-decks/${id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...deck, pages })
      });
      // Then publish
      const response = await fetch(`${API}/api/vision-decks/${id}/publish`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDeck({ ...deck, status: 'published', public_url: data.public_url });
        setPublishedData(data);
        setShowPublishSuccess(true);
        toast.success('Published successfully!');
      } else {
        toast.error('Failed to publish');
      }
    } catch (error) {
      toast.error('Error publishing deck');
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Link copied to clipboard!');
  };

  // Block Management
  const addBlock = (type) => {
    const newBlock = { id: Date.now().toString(), type, content: getDefaultBlockContent(type) };
    const updatedPages = [...pages];
    updatedPages[currentPageIndex].blocks.push(newBlock);
    setPages(updatedPages);
  };

  const updateBlock = (blockIndex, updatedBlock) => {
    const updatedPages = [...pages];
    updatedPages[currentPageIndex].blocks[blockIndex] = updatedBlock;
    setPages(updatedPages);
  };

  const deleteBlock = (blockIndex) => {
    const updatedPages = [...pages];
    updatedPages[currentPageIndex].blocks.splice(blockIndex, 1);
    setPages(updatedPages);
  };

  const duplicateBlock = (blockIndex) => {
    const updatedPages = [...pages];
    const blockToDuplicate = updatedPages[currentPageIndex].blocks[blockIndex];
    const newBlock = { ...JSON.parse(JSON.stringify(blockToDuplicate)), id: Date.now().toString() };
    updatedPages[currentPageIndex].blocks.splice(blockIndex + 1, 0, newBlock);
    setPages(updatedPages);
  };

  const moveBlock = (blockIndex, direction) => {
    const updatedPages = [...pages];
    const blocks = updatedPages[currentPageIndex].blocks;
    const newIndex = direction === 'up' ? blockIndex - 1 : blockIndex + 1;
    if (newIndex >= 0 && newIndex < blocks.length) {
      [blocks[blockIndex], blocks[newIndex]] = [blocks[newIndex], blocks[blockIndex]];
      setPages(updatedPages);
    }
  };

  // Page Management
  const addPage = () => {
    const newPage = { id: Date.now().toString(), name: `Page ${pages.length + 1}`, blocks: [] };
    setPages([...pages, newPage]);
    setCurrentPageIndex(pages.length);
  };

  const renamePage = (index, name) => {
    const updatedPages = [...pages];
    updatedPages[index].name = name;
    setPages(updatedPages);
  };

  const duplicatePage = (index) => {
    const pageToDuplicate = pages[index];
    const newPage = {
      ...JSON.parse(JSON.stringify(pageToDuplicate)),
      id: Date.now().toString(),
      name: `${pageToDuplicate.name || 'Page'} (Copy)`
    };
    const updatedPages = [...pages];
    updatedPages.splice(index + 1, 0, newPage);
    setPages(updatedPages);
    setCurrentPageIndex(index + 1);
  };

  const confirmDeletePage = (index) => {
    if (pages.length <= 1) {
      setPageToDelete(index);
      setShowDeleteConfirm(true);
    } else {
      deletePage(index);
    }
  };

  const deletePage = (index) => {
    if (pages.length <= 1) {
      // Replace with blank page
      setPages([{ id: Date.now().toString(), name: 'Page 1', blocks: [] }]);
      setCurrentPageIndex(0);
    } else {
      const updatedPages = pages.filter((_, i) => i !== index);
      setPages(updatedPages);
      if (currentPageIndex >= updatedPages.length) {
        setCurrentPageIndex(updatedPages.length - 1);
      } else if (currentPageIndex > index) {
        setCurrentPageIndex(currentPageIndex - 1);
      }
    }
    setShowDeleteConfirm(false);
    toast.success('Page deleted');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAF8]">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-[#B89B5E] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-500">Loading Vision Deck...</p>
        </div>
      </div>
    );
  }

  const currentPage = pages[currentPageIndex];
  const templateStyle = deck?.template_id || 'modern-executive';
  // Defensive: fall back to modern-executive when a deck was created with a
  // template_id the editor doesn't recognize (e.g. legacy 'lux-hotel' / 'minimal'
  // from earlier picker versions). Prevents `Cannot read properties of
  // undefined (reading 'name')` on style.name.
  const style = TEMPLATE_STYLES[templateStyle] || TEMPLATE_STYLES['modern-executive'];

  return (
    <div className="min-h-screen bg-[#F8F7F4]">
      {/* ========== PREMIUM HEADER ========== */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200/80">
        <div className="px-3 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-2">
          {/* Left Section */}
          <div className="flex items-center gap-2 sm:gap-6 flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" asChild className="hover:bg-gray-100 shrink-0">
                <Link to={location.state?.returnTo || '/vision-decks'} data-testid="editor-back-btn">
                  <ArrowLeft className="w-5 h-5" />
                </Link>
              </Button>
              
              {/* Mobile: Toggle Sidebar */}
              <Button variant="ghost" size="icon" className="lg:hidden hover:bg-gray-100 shrink-0" onClick={() => setShowSidebar(!showSidebar)}>
                <Layers className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <div className="hidden sm:flex w-8 h-8 rounded-lg bg-gradient-to-br from-[#B89B5E] to-[#8B7355] items-center justify-center shrink-0">
                <FileText className="w-4 h-4 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <Input
                  value={deck?.name || ''}
                  onChange={(e) => setDeck({ ...deck, name: e.target.value })}
                  className="text-base sm:text-lg font-medium border-none bg-transparent px-0 h-auto py-0 focus-visible:ring-0 placeholder:text-gray-400 w-full"
                  placeholder="Untitled Vision Deck"
                />
                <p className="text-xs text-gray-400 mt-0.5 hidden sm:block">{style.name} Template</p>
              </div>
            </div>
          </div>

          {/* Right Section - Actions */}
          <div className="flex items-center gap-1 sm:gap-3 shrink-0">
            <Button variant="ghost" size="icon" onClick={() => setShowSettings(true)} className="text-gray-600 hover:text-gray-900 sm:hidden">
              <Settings className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setShowSettings(true)} className="text-gray-600 hover:text-gray-900 hidden sm:flex">
              <Settings className="w-4 h-4 mr-2" />
              <span className="hidden md:inline">Settings</span>
            </Button>
            
            <div className="w-px h-6 bg-gray-200 hidden sm:block" />
            
            <Button variant="outline" size="icon" onClick={async () => { if (hasUnsavedChanges) await handleSave(); window.open(`/vision-decks/${id}/preview`, '_blank'); }} className="border-gray-200 sm:hidden">
              <Eye className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={async () => { if (hasUnsavedChanges) await handleSave(); window.open(`/vision-decks/${id}/preview`, '_blank'); }} className="border-gray-200 hidden sm:flex">
              <Eye className="w-4 h-4 mr-2" />
              <span className="hidden md:inline">Preview</span>
            </Button>
            
            {/* P1b — Save button gate: disabled when any service-adjuster is invalid, with tooltip */}
            {(() => {
              const invalidAdjusters = deckHasInvalidAdjusters(deck);
              const saveDisabled = saving || invalidAdjusters;
              const saveLabel = saving ? 'Saving...' : (hasUnsavedChanges ? 'Save*' : 'Saved');
              const tooltipMsg = invalidAdjusters
                ? 'Save blocked: one or more Service Adjusters has invalid Min/Max values. Fix the highlighted fields or use "Fix automatically".'
                : null;
              const SaveIconBtn = (
                <Button variant="outline" size="icon" onClick={handleSave} disabled={saveDisabled} className={`border-gray-200 sm:hidden ${invalidAdjusters ? 'border-red-300 bg-red-50' : (hasUnsavedChanges ? 'border-orange-300 bg-orange-50' : '')}`} data-testid="save-deck-btn-mobile">
                  {saving ? <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" /> : <Save className="w-4 h-4" />}
                </Button>
              );
              const SaveTextBtn = (
                <Button variant="outline" size="sm" onClick={handleSave} disabled={saveDisabled} className={`border-gray-200 hidden sm:flex ${invalidAdjusters ? 'border-red-300 bg-red-50 text-red-700' : (hasUnsavedChanges ? 'border-orange-300 bg-orange-50' : '')}`} data-testid="save-deck-btn">
                  {saving ? <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  <span className="hidden md:inline">{invalidAdjusters ? 'Fix Errors' : saveLabel}</span>
                </Button>
              );
              if (tooltipMsg) {
                return (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span data-testid="save-disabled-wrapper">{SaveIconBtn}</span>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">{tooltipMsg}</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span>{SaveTextBtn}</span>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">{tooltipMsg}</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                );
              }
              return <>{SaveIconBtn}{SaveTextBtn}</>;
            })()}
            
            <Button size="sm" onClick={handlePublish} disabled={saving} className="bg-[#B89B5E] hover:bg-[#A08050] text-white shadow-sm">
              <Send className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Publish</span>
            </Button>
          </div>
        </div>
      </header>

      {/* ========== MAIN EDITOR LAYOUT ========== */}
      <div className="flex h-[calc(100vh-65px)] sm:h-[calc(100vh-73px)]">
        {/* ========== LEFT SIDEBAR - PAGES ========== */}
        <aside className={`${showSidebar ? 'w-48 lg:w-56' : 'w-0'} bg-white border-r border-gray-200/80 flex flex-col transition-all duration-300 overflow-hidden absolute lg:relative z-40 h-full lg:h-auto`}>
          <div className="p-4 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-gray-700 uppercase tracking-wider">Pages</h3>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={addPage}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Add Page</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
          
          <ScrollArea className="flex-1 p-4">
            <div className="space-y-4">
              {pages.map((page, index) => (
                <PageThumbnail
                  key={page.id}
                  page={page}
                  index={index}
                  isActive={currentPageIndex === index}
                  onClick={() => setCurrentPageIndex(index)}
                  onRename={(name) => renamePage(index, name)}
                  onDuplicate={() => duplicatePage(index)}
                  onDelete={() => confirmDeletePage(index)}
                  canDelete={pages.length > 1}
                  style={style}
                />
              ))}
            </div>
          </ScrollArea>
        </aside>

        {/* ========== MAIN CANVAS ========== */}
        <main className="flex-1 overflow-auto bg-gradient-to-br from-[#F5F5F0] to-[#ECEAE5]" onClick={() => showSidebar && window.innerWidth < 1024 && setShowSidebar(false)}>
          <div className="min-h-full p-4 sm:p-8 lg:p-12">
            {/* Canvas Container */}
            <div 
              className="max-w-4xl mx-auto rounded-xl sm:rounded-2xl shadow-xl sm:shadow-2xl overflow-hidden"
              style={{ backgroundColor: style.background }}
            >
              {/* Canvas Content */}
              <div className="p-5 sm:p-10 lg:p-16 min-h-[500px] sm:min-h-[700px]">
                {/* Blocks */}
                <div className="space-y-6 sm:space-y-8">
                  {currentPage?.blocks.map((block, index) => (
                    <BlockRenderer
                      key={block.id}
                      block={block}
                      style={style}
                      isEditing={true}
                      onUpdate={(updated) => updateBlock(index, updated)}
                      onDelete={() => deleteBlock(index)}
                      onDuplicate={() => duplicateBlock(index)}
                      onMoveUp={() => moveBlock(index, 'up')}
                      onMoveDown={() => moveBlock(index, 'down')}
                      isFirst={index === 0}
                      isLast={index === currentPage.blocks.length - 1}
                      pages={pages}
                    />
                  ))}
                </div>

                {/* Add Block Button */}
                <button
                  onClick={() => setShowBlockPicker(true)}
                  className="w-full mt-8 sm:mt-10 py-8 sm:py-10 border-2 border-dashed border-gray-300/60 rounded-xl sm:rounded-2xl flex flex-col items-center justify-center gap-2 sm:gap-3 hover:border-[#B89B5E] hover:bg-[#B89B5E]/5 transition-all duration-300 group"
                >
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-gray-100 group-hover:bg-[#B89B5E]/10 flex items-center justify-center transition-colors">
                    <Plus className="w-6 h-6 sm:w-7 sm:h-7 text-gray-400 group-hover:text-[#B89B5E]" />
                  </div>
                  <div className="text-center">
                    <p className="font-medium text-gray-600 group-hover:text-[#B89B5E]">Add Content Block</p>
                    <p className="text-xs sm:text-sm text-gray-400 mt-1">Text, images, video, and more</p>
                  </div>
                </button>
              </div>
            </div>

            {/* Page Indicator */}
            <div className="text-center mt-4 sm:mt-6">
              <span className="text-sm text-gray-500">
                Page {pages.length > 0 ? currentPageIndex + 1 : 0} of {pages.length}
              </span>
            </div>
          </div>
        </main>
      </div>

      {/* ========== MODALS ========== */}
      
      {/* Add Block Modal */}
      <AddBlockModal 
        open={showBlockPicker} 
        onClose={() => setShowBlockPicker(false)} 
        onSelect={addBlock} 
      />

      {/* Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-light tracking-wide">Deck Settings</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Deck Name</label>
              <Input
                value={deck?.name || ''}
                onChange={(e) => setDeck({ ...deck, name: e.target.value })}
                className="bg-gray-50"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Template Style</label>
              <Select value={deck?.template_id || 'modern-executive'} onValueChange={(value) => setDeck({ ...deck, template_id: value })}>
                <SelectTrigger className="bg-gray-50">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(TEMPLATE_STYLES).map(([key, val]) => (
                    <SelectItem key={key} value={key}>
                      <div className="flex items-center gap-3">
                        <div className="w-4 h-4 rounded" style={{ backgroundColor: val.accent }} />
                        {val.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSettings(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Page Confirmation */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Last Page?</DialogTitle>
            <DialogDescription>
              This is the only page in your deck. Deleting it will create a new blank page.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
            <Button variant="destructive" onClick={() => deletePage(pageToDelete)}>Delete & Create Blank</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Publish Success Dialog */}
      <Dialog open={showPublishSuccess} onOpenChange={setShowPublishSuccess}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <Check className="w-6 h-6 text-green-600" />
              </div>
              Vision Deck Published!
            </DialogTitle>
            <DialogDescription>
              Your deck has been published and an email draft has been created.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Shareable Link */}
            <div className="p-4 bg-gray-50 rounded-lg space-y-2">
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Shareable Link</label>
              <div className="flex items-center gap-2">
                <Input 
                  readOnly 
                  value={`${window.location.origin}${publishedData?.public_url || ''}`}
                  className="bg-white text-sm"
                />
                <Button 
                  variant="outline" 
                  size="icon"
                  onClick={() => copyToClipboard(`${window.location.origin}${publishedData?.public_url || ''}`)}
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-gray-500">Use this link in emails or share directly with clients</p>
            </div>

            {/* Actions */}
            <div className="grid grid-cols-2 gap-3">
              <Button 
                variant="outline" 
                className="h-auto py-4 flex flex-col items-center gap-2"
                onClick={() => {
                  setShowPublishSuccess(false);
                  window.open(`/vision-decks/${id}/preview`, '_blank');
                }}
              >
                <Eye className="w-5 h-5 text-gray-500" />
                <span className="text-sm">Preview Deck</span>
              </Button>
              
              <Button 
                className="h-auto py-4 flex flex-col items-center gap-2 bg-[#B89B5E] hover:bg-[#A08050]"
                onClick={() => {
                  setShowPublishSuccess(false);
                  navigate(`/email-designer?draft=${publishedData?.email_draft_id}`);
                }}
              >
                <Send className="w-5 h-5" />
                <span className="text-sm">Open Email Draft</span>
              </Button>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowPublishSuccess(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
